<template>
  <el-dialog :model-value="modelValue" title="上传资产" width="760px" class="video-library-upload" append-to-body
    :close-on-click-modal="false" :close-on-press-escape="!busy" :show-close="!busy" @update:model-value="close">
    <div class="library-upload-target">上传到当前剧本资产库</div>
    <div class="library-upload-actions">
      <el-button :disabled="busy || !allowed" @click="fileInput?.click()"><el-icon><Upload /></el-icon>选择文件</el-button>
      <el-button :disabled="busy || !allowed" @click="folderInput?.click()"><el-icon><FolderOpened /></el-icon>选择文件夹</el-button>
      <span>可多选，文件夹包含子目录</span>
      <input ref="fileInput" class="library-upload-file-input" type="file" multiple :accept="assetUploadAccept" hidden :disabled="busy || !allowed" @change="chooseFiles" />
      <input ref="folderInput" class="library-upload-folder-input" type="file" multiple webkitdirectory directory hidden :disabled="busy || !allowed" @change="chooseFiles" />
    </div>
    <div class="library-upload-drop" :class="{ dragging }" @dragover.prevent="dragging = !busy" @dragleave.prevent="dragging = false" @drop.prevent="dropFiles">
      <template v-if="!files.length"><el-icon><Upload /></el-icon><strong>选择或拖入图片、视频、音频</strong><span>图片不超过 15MB，视频和音频不超过 500MB</span></template>
      <template v-else>
        <div class="library-upload-summary"><span>共 {{ files.length }} 个文件</span><div><button type="button" :disabled="busy" @click="setRealPerson(true)">图片全设为真人</button><button type="button" :disabled="busy" @click="setRealPerson(false)">全设为非真人</button></div></div>
        <div class="library-upload-list">
          <div v-for="item in files" :key="item.key" class="library-upload-row">
            <img v-if="item.preview" :src="item.preview" alt="" /><el-icon v-else><component :is="item.type === 'Video' ? VideoCamera : Headset" /></el-icon>
            <div class="library-upload-file-info"><input v-model="item.name" :aria-label="`素材名称 ${item.file.name}`" :disabled="busy" maxlength="64" /><small :title="item.file.webkitRelativePath || item.file.name">{{ item.file.webkitRelativePath || item.file.name }}</small><span v-if="item.error" class="library-upload-error">{{ item.error }}</span></div>
            <label v-if="item.type === 'Image'" class="library-upload-person"><input v-model="item.isRealPerson" type="checkbox" :disabled="busy" />真人</label>
            <span v-else class="library-upload-type">{{ item.type === 'Video' ? '视频' : '音频' }}</span>
            <button type="button" class="library-upload-remove" :aria-label="`移除待上传 ${item.file.name}`" :disabled="busy" @click="remove(item)"><el-icon><Close /></el-icon></button>
          </div>
        </div>
      </template>
    </div>
    <div v-if="notice" class="library-upload-notice" role="status">{{ notice }}</div>
    <p class="library-upload-tip">真人图片审核通过后会出现在资产列表中。</p>
    <template #footer><el-button :disabled="busy" @click="close">取消</el-button><el-button type="primary" :loading="busy" :disabled="!files.length || !allowed || !scriptId" @click="submit">{{ busy ? `上传中 ${progress}` : '开始上传' }}</el-button></template>
  </el-dialog>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { Upload, FolderOpened, VideoCamera, Headset, Close } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getToken, hasPermission } from '@/api/auth'
import { ensureScriptAssetGroup, uploadAsset, getAssetStatus } from '@/api/volcAsset'
import { uploadAssetReference } from '@/utils/referenceUpload'
import { notifyVolcAssetsChanged } from '@/utils/volcAssetEvents'
import { assetUploadAccept, assetUploadType, assetUploadFileError, assetUploadFileKey, assetUploadDuration } from '../../volc-asset/assetUploadFiles'
const props = defineProps({ modelValue: Boolean, scriptId: { type: [String, Number], default: null } })
const emit = defineEmits(['update:modelValue', 'busy'])
const allowed = computed(() => hasPermission('volc_asset:upload') && hasPermission('file:upload'))
const fileInput = ref(null), folderInput = ref(null), files = ref([]), busy = ref(false), dragging = ref(false), notice = ref(''), progress = ref('')
let disposed = false, pollTimer = null
const pendingReviews = new Map()
function release(item) { if (item.preview) URL.revokeObjectURL(item.preview) }
function clear() { files.value.forEach(release); files.value = []; notice.value = ''; dragging.value = false }
function close() { if (!busy.value) { emit('update:modelValue', false); clear() } }
function addFiles(incoming) {
  if (busy.value || !allowed.value) return
  const keys = new Set(files.value.map(item => item.key)), skipped = []
  for (const file of incoming) {
    const error = assetUploadFileError(file)
    if (error) { skipped.push(`${file.name}：${error}`); continue }
    const key = assetUploadFileKey(file)
    if (keys.has(key)) continue
    keys.add(key)
    const type = assetUploadType(file.name)
    files.value.push({ file, key, type, isRealPerson: type === 'Image', name: file.name.replace(/\.[^.]+$/, '').slice(0, 64),
      preview: type === 'Image' ? URL.createObjectURL(file) : '', error: '' })
  }
  notice.value = skipped.length ? `跳过 ${skipped.length} 个文件：${skipped.slice(0, 3).join('；')}` : ''
}
function chooseFiles(event) { addFiles(Array.from(event.target.files || [])); event.target.value = '' }
function dropFiles(event) { dragging.value = false; addFiles(Array.from(event.dataTransfer?.files || [])) }
function remove(item) { release(item); files.value = files.value.filter(file => file.key !== item.key) }
function setRealPerson(value) { files.value.forEach(item => { if (item.type === 'Image') item.isRealPerson = value }) }
function changed(scriptId, groupId) { notifyVolcAssetsChanged({ source: 'video-library-upload', scriptId, groupId }) }
function scheduleReviews() {
  if (disposed || pollTimer || !pendingReviews.size) return
  pollTimer = setTimeout(async () => {
    pollTimer = null
    for (const [id, review] of pendingReviews) {
      if (disposed || review.scriptId !== props.scriptId || review.token !== getToken() || !hasPermission('volc_asset:view')) { pendingReviews.delete(id); continue }
      try {
        const res = await getAssetStatus(id)
        if (disposed || review.scriptId !== props.scriptId || review.token !== getToken()) continue
        if (res.code === 200 && res.data?.status !== 'Processing') {
          pendingReviews.delete(id)
          if (res.data?.status === 'Active') changed(review.scriptId, review.groupId)
          else if (res.data?.status === 'Failed') ElMessage.warning(`${review.name} 审核未通过`)
        }
      } catch (_) { /* A temporary status failure retries on the next tick. */ }
      if (++review.attempts >= 60) pendingReviews.delete(id)
    }
    scheduleReviews()
  }, 5000)
}
async function submit() {
  if (busy.value || !allowed.value || !props.scriptId || !files.value.length) return
  const scriptId = props.scriptId, token = getToken(), batch = [...files.value]
  const current = () => !disposed && props.scriptId === scriptId && token === getToken() && allowed.value
  busy.value = true; emit('busy', true); notice.value = ''; progress.value = `0 / ${batch.length}`
  let succeeded = 0, groupId
  try {
    const group = await ensureScriptAssetGroup(scriptId)
    if (!current()) return
    if (group.code !== 200 || !group.data?.groupId) throw new Error(group.message || '当前剧本资产库暂不可用')
    groupId = group.data.groupId
    for (const [index, item] of batch.entries()) {
      if (!current()) break
      progress.value = `${index + 1} / ${batch.length}`; item.error = ''
      try {
        // Preserve a completed file transfer when retrying asset registration.
        if (!item.uploaded) item.uploaded = await uploadAssetReference(item.file, item.type)
        if (!current()) break
        const durationMs = await assetUploadDuration(item.file, item.type)
        if (!current()) break
        const res = await uploadAsset({ groupId, ...item.uploaded, assetName: item.name || item.file.name.replace(/\.[^.]+$/, ''),
          assetType: item.type, isRealPerson: item.type === 'Image' && item.isRealPerson, durationMs })
        if (res.code !== 200) throw new Error(res.message || '上传失败')
        succeeded++
        if (!current()) break
        if (res.data?.status === 'Processing' && res.data.id) pendingReviews.set(res.data.id, { scriptId, groupId, token, attempts: 0, name: item.name })
        remove(item)
      } catch (error) { if (current()) item.error = error.message || '上传失败，请重试' }
    }
    if (succeeded) {
      changed(scriptId, groupId)
      scheduleReviews()
    }
    if (current()) {
      notice.value = `成功上传 ${succeeded} 个${files.value.length ? `，剩余 ${files.value.length} 个可重试` : ''}`
      if (!files.value.length) { ElMessage.success(notice.value); emit('update:modelValue', false) }
    }
  } catch (error) { if (current()) notice.value = error.message || '上传失败，请重试' }
  finally { busy.value = false; emit('busy', false) }
}
watch(() => props.scriptId, () => { emit('update:modelValue', false); clear(); pendingReviews.clear(); clearTimeout(pollTimer); pollTimer = null })
watch(() => props.modelValue, open => { if (open && !busy.value) clear() })
onBeforeUnmount(() => { disposed = true; clearTimeout(pollTimer); pendingReviews.clear(); clear() })
</script>

<style scoped>
:global(.video-library-upload.el-dialog) { max-width: calc(100vw - 32px); padding: 24px; border: 1px solid #454545; border-radius: 18px; background: #212121; color: #ddd; color-scheme: dark; --el-text-color-primary: #eee; --el-text-color-regular: #ccc; --el-fill-color-blank: #292929; --el-color-primary: #ccc; --el-border-color: #484848; }
.library-upload-target { margin-bottom: 16px; color: #aaa; font-size: 12px; }
.library-upload-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.library-upload-actions .el-button { margin: 0; }
.library-upload-actions .el-icon { margin-right: 6px; }
.library-upload-actions > span { color: #888; font-size: 12px; }
.library-upload-drop { border: 1px dashed #505050; border-radius: 12px; padding: 14px; min-height: 180px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; background: #262626; }
.library-upload-drop.dragging { border-color: #ddd; background: #333; }
.library-upload-drop > .el-icon { font-size: 28px; color: #aaa; }
.library-upload-drop > strong { font-size: 14px; font-weight: 400; }
.library-upload-drop > span { font-size: 12px; color: #888; }
.library-upload-summary { display: flex; justify-content: space-between; gap: 10px; width: 100%; font-size: 12px; color: #aaa; }
.library-upload-summary button { border: 0; padding: 0 6px; background: transparent; color: #ccc; font: inherit; cursor: pointer; }
.library-upload-list { width: 100%; max-height: 45vh; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #555 transparent; }
.library-upload-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #383838; }
.library-upload-row > img, .library-upload-row > .el-icon { flex-shrink: 0; width: 42px; height: 42px; border-radius: 6px; object-fit: cover; background: #333; font-size: 22px; }
.library-upload-file-info { display: flex; flex: 1; flex-direction: column; gap: 5px; min-width: 0; }
.library-upload-file-info > input { width: 100%; box-sizing: border-box; padding: 6px; border: 1px solid #484848; border-radius: 6px; background: #292929; color: #ddd; }
.library-upload-file-info small { color: #888; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.library-upload-person { display: flex; align-items: center; white-space: nowrap; color: #bbb; font-size: 12px; }
.library-upload-type { color: #999; font-size: 12px; }
.library-upload-remove { border: 0; padding: 4px; border-radius: 5px; background: #333; color: #ccc; cursor: pointer; }
.library-upload-notice, .library-upload-tip { margin: 12px 0 0; font-size: 12px; color: #aaa; line-height: 1.6; }
.library-upload-error { color: #e6a4a4; font-size: 11px; }
:global(.video-library-upload .el-button--primary) { background: #e3e3e3; border-color: #e3e3e3; color: #171717; }
@media (max-width: 520px) { .library-upload-summary { flex-direction: column; } .library-upload-row { gap: 6px; } }
</style>
